import { designVariables } from "@studio3t/design-variables";
import { Icon, IconSVG, SVGColorKey } from "@studio3t/generic-components";
import styled from "styled-components";
import { EDITOR_FONT, JsonViewer } from "@/components/JsonViewer";
import type { TestRunCaseView, TestRunView } from "./scriptRun";

// The Output panel's test-run view — old /studio's OutputPanel test faces
// restyled onto the V2 card: the verdict header (Confirm/Error icon +
// count or name), one row per case on suite runs, then every assertion
// that ran as its own verdict line (the assertion as the editor writes
// it; failed lines keep the red card with the received value). A
// single-case run's actual output document goes to the split view's
// right pane (TestRunActualOutput — StudioPanels' TestRunSplit hosts
// both sides).

// A case's verdict line — the same face for a singular run's header and
// each row of a suite run.
function caseVerdictText(c: TestRunCaseView): string {
  return c.passed ? `Test passed: ${c.name}` : `Test failed: ${c.name}`;
}

// A single-case run's verdict names the test; a suite run counts.
function verdictText(view: TestRunView): string {
  if (view.total === 1) return caseVerdictText(view.cases[0]);
  return `${view.passed}/${view.total} tests passed`;
}

// A single-case run with its actual document splits the Output panel —
// test data left, output document right. Suite runs (and a single case
// that never produced a document) keep the stacked list.
export function splitsTestRun(
  view: TestRunView,
): view is TestRunView & { actual: string } {
  return view.total === 1 && view.actual !== undefined;
}

export function TestRunResult({ view }: { view: TestRunView }) {
  const allPassed = view.passed === view.total;
  return (
    <ResultsBox data-testid="test-run-result">
      <Verdict $passed={allPassed}>
        <VerdictIcon passed={allPassed} />
        {verdictText(view)}
      </Verdict>
      {/* A suite run lists every case; the single run's only case is
          already the header, so just its assertion lines follow. */}
      {view.total > 1 ? (
        view.cases.map((c) => <CaseRow key={c.name} result={c} />)
      ) : (
        <CaseAssertions result={view.cases[0]} />
      )}
    </ResultsBox>
  );
}

// The split view's right pane: the single-case run's actual output
// document — what the assertions ran against — in the read-only viewer.
export function TestRunActualOutput({ actual }: { actual: string }) {
  return (
    <ActualSection data-testid="test-run-actual-output">
      <SectionLabel>Actual output</SectionLabel>
      <ViewerFrame>
        <JsonViewer value={actual} />
      </ViewerFrame>
    </ActualSection>
  );
}

function VerdictIcon({ passed }: { passed: boolean }) {
  return (
    <IconSVG
      icon={passed ? Icon.Confirm : Icon.Error}
      size={12}
      svgColorKey={passed ? SVGColorKey.confirmIcon : SVGColorKey.errorIcon}
    />
  );
}

// A suite run's case row wears the singular run's verdict face —
// "Test passed/failed: name" in the same bold size and pass/fail color.
function CaseRow({ result }: { result: TestRunCaseView }) {
  return (
    <CaseBlock>
      <Verdict $passed={result.passed}>
        <VerdictIcon passed={result.passed} />
        {caseVerdictText(result)}
      </Verdict>
      <CaseAssertions result={result} />
    </CaseBlock>
  );
}

// A case's detail: its runtime error (it never reached the assertions),
// or every assertion that ran with its own verdict icon — the line
// EXACTLY as the editor writes it (path, operator glyph, expected value,
// in the viewer face's palette). Failed lines keep the red card with
// what the pipeline actually produced; passed lines stay plain, so the
// mixed verdicts of same-path ==/!= assertions read apart at a glance.
function CaseAssertions({ result }: { result: TestRunCaseView }) {
  if (!result.error && result.assertions.length === 0) return null;
  // Duplicate path+op pairs get an occurrence-numbered key (the list is
  // an immutable run snapshot, never reordered).
  const seen = new Map<string, number>();
  return (
    <AssertionsList>
      {result.error && <ErrorCard>{result.error}</ErrorCard>}
      {result.assertions.map((line) => {
        const at = `${line.path} ${line.op}`;
        const nth = seen.get(at) ?? 0;
        seen.set(at, nth + 1);
        const key = `${nth}:${at}`;
        if (line.passed) {
          return (
            <PassedLine key={key} data-testid="assertion-passed">
              <VerdictIcon passed />
              <AssertionLine>
                <AssertionDim>{line.path}</AssertionDim>
                <AssertionDim>{line.op}</AssertionDim>
                <AssertionValue>{line.expected}</AssertionValue>
              </AssertionLine>
            </PassedLine>
          );
        }
        return (
          <FailureCard key={key} data-testid="assertion-failed">
            <FailedLine>
              <VerdictIcon passed={false} />
              <AssertionLine>
                <AssertionDim>{line.path}</AssertionDim>
                <AssertionDim>{line.op}</AssertionDim>
                <AssertionValue>{line.expected}</AssertionValue>
              </AssertionLine>
            </FailedLine>
            <ReceivedLine>
              Received: <Value>{line.received}</Value>
            </ReceivedLine>
          </FailureCard>
        );
      })}
    </AssertionsList>
  );
}

const ResultsBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${designVariables.ui.semantic.spacings}px;
  padding: ${designVariables.ui.semantic.spacingm}px;
`;

const Verdict = styled.div<{ $passed: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${(p) =>
    p.$passed
      ? p.theme.badgeConfirmSubtleColor
      : p.theme.badgeErrorSubtleColor};
`;

const CaseBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

// Indented under the 12px icon + gap of the row above.
const AssertionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-left: 18px;
`;

// A passed assertion as a Bridge_TestLine row (the tests editor panel's
// viewer geometry, 36px) with the verdict icon leading — the icon column
// plus the card's horizontal inset (12px padding + 3px border) so the
// assertion text columns line up with the failed cards below/above it.
const PassedLine = styled.div`
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px 0 15px;
`;

// The failed card's leading line: verdict icon + the assertion as
// written.
const FailedLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FailureCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 12px;
  background: ${(p) => p.theme.badgeErrorSubtleBackground};
  border-left: 3px solid ${(p) => p.theme.statusError};
  border-radius: 4px;
  color: ${(p) => p.theme.semanticTextBody};
`;

// A runtime error keeps the plain mono card — there is nothing to
// compare, just the message.
const ErrorCard = styled(FailureCard)`
  font-family: ${EDITOR_FONT};
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
`;

// The assertion as written: the tests editor panel's Bridge_TestLine
// face (TestsEditorPanel ViewLine) — Inter Medium 14 at 1.2, path and
// operator in textlight, the expected value in textheading, single line
// clipped at the edge.
const AssertionLine = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${designVariables.ui.semantic.spacings}px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  font-variant-numeric: lining-nums tabular-nums;
  white-space: nowrap;
  overflow: hidden;
`;

const AssertionDim = styled.span`
  flex-shrink: 0;
  color: ${(p) => p.theme.semanticTextLight};
`;

const AssertionValue = styled.span`
  color: ${(p) => p.theme.semanticTextHeading};
  overflow: hidden;
  text-overflow: ellipsis;
`;

// What the pipeline actually produced, in error red — indented under the
// icon column (12px icon + 8px gap) to line up with the assertion text.
const ReceivedLine = styled.div`
  font-size: 13px;
  line-height: 1.4;
  color: ${(p) => p.theme.badgeErrorSubtleColor};
  padding-left: 20px;
  word-break: break-word;
`;

const Value = styled.span`
  font-weight: 600;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: ${(p) => p.theme.semanticTextLight};
`;

// Fills the split's right pane with the ResultsBox spacing so both sides
// align under the Output bar.
const ActualSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: ${designVariables.ui.semantic.spacingm}px;
`;

const ViewerFrame = styled.div`
  border: 1px solid ${(p) => p.theme.semanticBorderLight};
  border-radius: 4px;
  overflow: hidden;
`;
